import { useState, useEffect } from 'react';
import { useCart } from '../../hooks/useCart';
import StockDisplay from '../../components/feature/StockDisplay';
import ProductBadge from '../../components/base/ProductBadge';
import ProductButton from '../../components/base/ProductButton';
import { useNavigate } from 'react-router-dom';

export default function ProduitDeuxCoeursUneHistoire() {
  const { addItem } = useCart();
  const navigate = useNavigate();
  const [quantity, setQuantity] = useState(1);
  const [isAdding, setIsAdding] = useState(false);
  const [addedToCart, setAddedToCart] = useState(false);
  const [stockInfo, setStockInfo] = useState<{available: boolean} | null>(null);

  useEffect(() => {
    // SEO JSON-LD pour le produit
    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": "Deux cœurs, une histoire",
      "description": "Création artisanale en livre plié représentant deux cœurs liés. Symbole d'amour, complicité et fidélité. Pièce unique faite main.",
      "image": "https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/8306a3be3e6bcfd00f47354001f0efb5.png",
      "brand": {
        "@type": "Brand",
        "name": "Les Carnets de Lolotte"
      },
      "offers": {
        "@type": "Offer",
        "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/produit-deux-coeurs-une-histoire`,
        "priceCurrency": "EUR",
        "price": "30.00",
        "availability": "https://schema.org/InStock",
        "itemCondition": "https://schema.org/NewCondition"
      },
      "category": "Collection Saint-Valentin",
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "5",
        "reviewCount": "1"
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(jsonLd);
    document.head.appendChild(script);

    // Mise à jour des meta tags
    document.title = "Deux cœurs, une histoire – Création artisanale Saint-Valentin | Les Carnets de Lolotte";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Deux cœurs liés façonnés dans un livre plié. Création artisanale unique symbolisant amour et complicité. Cadeau romantique fait main pour Saint-Valentin, mariage ou occasion spéciale.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'cadeau Saint-Valentin, création romantique, livre plié cœur, cadeau couple, symbole amour, artisanat français, Les Carnets de Lolotte');
    }

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const product = {
    id: 'deux-coeurs-une-histoire',
    name: 'Deux cœurs, une histoire',
    price: 30,
    description: "Cette création en livre plié représente deux cœurs liés, façonnés dans les pages d'un même ouvrage. Elle symbolise un lien profond, une histoire construite page après page : amour, complicité, fidélité.",
    category: 'Collection Saint-Valentin',
    subcategory: 'Symboles & spiritualité douce',
    image: 'https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/8306a3be3e6bcfd00f47354001f0efb5.png',
    features: [
      'Création artisanale en livre plié',
      'Pièce unique',
      'Réalisée entièrement à la main',
      'Objet décoratif poétique et symbolique',
      'Symbole d\'amour et de complicité',
      'Idée cadeau pour couple, mariage, Saint-Valentin'
    ]
  };

  const handleAddToCart = async () => {
    if (!stockInfo?.available) {
      return;
    }

    setIsAdding(true);

    try {
      addItem({
        id: product.id,
        name: product.name,
        price: product.price,
        image: product.image,
        quantity: quantity
      });

      setAddedToCart(true);
      
      setTimeout(() => {
        setAddedToCart(false);
      }, 3000);

    } catch (error) {
      console.error('Erreur lors de l\'ajout au panier:', error);
      alert('Erreur lors de l\'ajout au panier. Veuillez réessayer.');
    } finally {
      setIsAdding(false);
    }
  };

  const getButtonText = () => {
    if (isAdding) return 'Ajout en cours...';
    if (addedToCart) return 'Ajouté au panier !';
    if (!stockInfo?.available) return 'Rupture de stock';
    return `Ajouter au panier • ${product.price * quantity}€`;
  };

  const isButtonDisabled = isAdding || !stockInfo?.available;

  return (
    <div className="min-h-screen bg-brand-rose-50">
      <main className="pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <nav className="flex items-center space-x-2 text-sm text-brand-text-secondary mb-8">
            <a href="/" className="hover:text-brand-rose-500 transition-colors cursor-pointer" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Accueil
            </a>
            <i className="ri-arrow-right-s-line text-brand-rose-400"></i>
            <a href="/boutique" className="hover:text-brand-rose-500 transition-colors cursor-pointer" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Boutique
            </a>
            <i className="ri-arrow-right-s-line text-brand-rose-400"></i>
            <span className="text-brand-rose-600 font-medium" style={{ fontFamily: 'Poppins, sans-serif' }}>{product.name}</span>
          </nav>

          <section className="py-8 sm:py-16">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12">
                {/* Galerie d'images */}
                <div className="space-y-4">
                  <div className="aspect-square bg-brand-beige-100 rounded-lg border-2 border-brand-rose-400 overflow-hidden">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>

                {/* Informations produit */}
                <div className="space-y-6">
                  <div>
                    <div className="flex flex-wrap gap-2 mb-3">
                      <ProductBadge type="unique" className="text-sm">
                        {product.category}
                      </ProductBadge>
                      <ProductBadge type="nouveau" className="text-sm">
                        {product.subcategory}
                      </ProductBadge>
                    </div>
                    <h1 className="text-3xl sm:text-4xl font-bold text-brand-text-primary mt-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                      {product.name}
                    </h1>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <ProductBadge type="unique">
                      Fait main ✋
                    </ProductBadge>
                    <ProductBadge type="unique">
                      Pièce unique ✨
                    </ProductBadge>
                    <ProductBadge type="nouveau">
                      Saint-Valentin 💕
                    </ProductBadge>
                  </div>

                  <div className="space-y-4">
                    <p className="text-lg text-brand-text-primary leading-relaxed font-semibold italic" style={{ fontFamily: 'Playfair Display, serif' }}>
                      Deux cœurs, une histoire.
                    </p>
                    <p className="text-base sm:text-lg text-brand-text-primary leading-relaxed" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      {product.description}
                    </p>
                    <p className="text-base sm:text-lg text-brand-text-primary leading-relaxed" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      Chaque pièce est réalisée entièrement à la main, avec patience et soin. Aucun exemplaire ne se ressemble parfaitement, car chaque livre a son propre vécu, sa texture, ses nuances.
                    </p>
                    <p className="text-base sm:text-lg text-brand-text-primary leading-relaxed italic" style={{ fontFamily: 'Playfair Display, serif' }}>
                      Offrir cette création, c'est offrir une histoire, un lien, une émotion qui traverse le temps.
                    </p>
                  </div>

                  <div className="flex items-center space-x-3">
                    <span className="text-3xl sm:text-4xl font-bold text-brand-rose-600" style={{ fontFamily: 'Playfair Display, serif' }}>
                      {product.price}€
                    </span>
                  </div>

                  {/* Affichage du stock */}
                  <StockDisplay 
                    productId={product.id}
                    onStockChange={setStockInfo}
                    className="mb-4"
                  />

                  <div className="bg-brand-beige-100 border-2 border-brand-rose-400 rounded-lg p-4 sm:p-6 space-y-3">
                    <h3 className="text-lg font-semibold text-brand-text-primary mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                      Caractéristiques
                    </h3>
                    <ul className="space-y-2">
                      {product.features.map((feature, index) => (
                        <li key={index} className="flex items-start text-brand-text-primary" style={{ fontFamily: 'Poppins, sans-serif' }}>
                          <i className="ri-heart-line text-brand-rose-500 mr-2 mt-1 flex-shrink-0"></i>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center space-x-4">
                      <span className="font-medium text-brand-text-primary" style={{ fontFamily: 'Playfair Display, serif' }}>Quantité :</span>
                      <div className="flex items-center border-2 border-brand-rose-400 rounded-lg">
                        <ProductButton
                          variant="quantity"
                          size="sm"
                          onClick={() => setQuantity(Math.max(1, quantity - 1))}
                          icon="ri-subtract-line"
                        >
                        </ProductButton>
                        <span className="px-4 py-2 font-medium text-brand-text-primary" style={{ fontFamily: 'Playfair Display, serif' }}>{quantity}</span>
                        <ProductButton
                          variant="quantity"
                          size="sm"
                          onClick={() => setQuantity(quantity + 1)}
                          icon="ri-add-line"
                        >
                        </ProductButton>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-4">
                      <ProductButton
                        variant="primary"
                        size="lg"
                        onClick={handleAddToCart}
                        disabled={isButtonDisabled}
                        loading={isAdding}
                        icon={addedToCart ? "ri-check-line" : "ri-shopping-cart-line"}
                        className="flex-1"
                      >
                        {getButtonText()}
                      </ProductButton>
                      <ProductButton
                        variant="secondary"
                        size="lg"
                        onClick={() => navigate('/panier')}
                        icon="ri-shopping-bag-line"
                      >
                        Voir le panier
                      </ProductButton>
                    </div>
                  </div>

                  {stockInfo?.available && (
                    <div className="flex items-center text-green-600">
                      <i className="ri-check-line mr-2"></i>
                      <span style={{ fontFamily: 'Poppins, sans-serif' }}>En stock - Expédition sous 2-3 jours</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>

          {/* Section descriptive */}
          <section className="py-12 sm:py-16 bg-[#FAF9F8] rounded-lg">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <h2 className="text-3xl sm:text-4xl font-bold text-[#B97A72] mb-6" style={{ fontFamily: 'Allura, cursive' }}>
                Un symbole d'amour intemporel
              </h2>
              <p className="text-base sm:text-lg text-[#6B5E5A] leading-relaxed mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Cette création unique en livre plié célèbre l'amour sous toutes ses formes. Deux cœurs entrelacés, 
                façonnés page après page dans un même livre, symbolisent l'union, la complicité et la fidélité.
              </p>
              <p className="text-base sm:text-lg text-[#6B5E5A] leading-relaxed mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Chaque pièce est réalisée entièrement à la main avec patience et délicatesse. Les pages pliées 
                créent un relief unique qui capte la lumière et donne vie à cette sculpture de papier.
              </p>
              <p className="text-base sm:text-lg text-[#6B5E5A] leading-relaxed italic" style={{ fontFamily: 'Playfair Display, serif' }}>
                Un cadeau parfait pour la Saint-Valentin, un mariage, un anniversaire de couple ou toute occasion 
                spéciale où vous souhaitez exprimer vos sentiments les plus profonds.
              </p>
            </div>
          </section>

          {/* Section idées cadeaux */}
          <section className="py-12 sm:py-16">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-2xl sm:text-3xl font-bold text-[#C49B86] mb-8 text-center" style={{ fontFamily: 'Allura, cursive' }}>
                Occasions parfaites pour offrir
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="bg-[#FAF9F8] border-2 border-[#B97A72] rounded-lg p-6 text-center">
                  <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3">
                    <i className="ri-heart-3-line text-4xl text-[#B97A72]"></i>
                  </div>
                  <h3 className="text-lg font-semibold text-[#6B5E5A] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Saint-Valentin
                  </h3>
                  <p className="text-sm text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    Déclarez votre amour avec poésie
                  </p>
                </div>
                <div className="bg-[#FAF9F8] border-2 border-[#B97A72] rounded-lg p-6 text-center">
                  <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3">
                    <i className="ri-cake-3-line text-4xl text-[#B97A72]"></i>
                  </div>
                  <h3 className="text-lg font-semibold text-[#6B5E5A] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Mariage
                  </h3>
                  <p className="text-sm text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    Célébrez l'union de deux âmes
                  </p>
                </div>
                <div className="bg-[#FAF9F8] border-2 border-[#B97A72] rounded-lg p-6 text-center">
                  <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3">
                    <i className="ri-calendar-2-line text-4xl text-[#B97A72]"></i>
                  </div>
                  <h3 className="text-lg font-semibold text-[#6B5E5A] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Anniversaire de couple
                  </h3>
                  <p className="text-sm text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    Marquez les années de complicité
                  </p>
                </div>
                <div className="bg-[#FAF9F8] border-2 border-[#B97A72] rounded-lg p-6 text-center">
                  <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3">
                    <i className="ri-gift-line text-4xl text-[#B97A72]"></i>
                  </div>
                  <h3 className="text-lg font-semibold text-[#6B5E5A] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Occasion spéciale
                  </h3>
                  <p className="text-sm text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    Pour tous les moments importants
                  </p>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
